import React from 'react';
import { CheckCircle, XCircle, ShieldAlert, Users, TrendingUp, Activity } from 'lucide-react';
import type { SurveyRecord } from '@/types/survey';
import Icon from '@/components/ui/AppIcon';


interface StatsPanelProps {
  records: SurveyRecord[];
}

export default function StatsPanel({ records }: StatsPanelProps) {
  const total = records.length;
  const completeCount = records.filter((r) => r.status === 'complete').length;
  const terminateCount = records.filter((r) => r.status === 'terminate').length;
  const qualityCount = records.filter((r) => r.status === 'qualitySecurity').length;
  const quotaCount = records.filter((r) => r.status === 'quotaFull').length;

  const completionRate = total > 0 ? Math.round((completeCount / total) * 100) : 0;
  const terminateRate = total > 0 ? Math.round((terminateCount / total) * 100) : 0;
  const qualityRate = total > 0 ? Math.round((qualityCount / total) * 100) : 0;
  const quotaRate = total > 0 ? Math.round((quotaCount / total) * 100) : 0;

  const stats = [
    {
      key: 'stat-complete',
      label: 'Complete',
      count: completeCount,
      rate: completionRate,
      icon: CheckCircle,
      gradientClass: 'gradient-complete',
      iconColor: 'text-green-400',
      rateColor: 'text-green-400',
      barColor: 'bg-green-500',
      delay: 'fade-in-delay-1',
    },
    {
      key: 'stat-terminate',
      label: 'Terminate',
      count: terminateCount,
      rate: terminateRate,
      icon: XCircle,
      gradientClass: 'gradient-terminate',
      iconColor: 'text-red-400',
      rateColor: 'text-red-400',
      barColor: 'bg-red-500',
      delay: 'fade-in-delay-2',
    },
    {
      key: 'stat-quality',
      label: 'Quality / Security',
      count: qualityCount,
      rate: qualityRate,
      icon: ShieldAlert,
      gradientClass: 'gradient-quality',
      iconColor: 'text-purple-400',
      rateColor: 'text-purple-400',
      barColor: 'bg-purple-500',
      delay: 'fade-in-delay-3',
    },
    {
      key: 'stat-quota',
      label: 'Quota Full',
      count: quotaCount,
      rate: quotaRate,
      icon: Users,
      gradientClass: 'gradient-quota',
      iconColor: 'text-amber-400',
      rateColor: 'text-amber-400',
      barColor: 'bg-amber-500',
      delay: 'fade-in-delay-4',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Summary row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-primary" />
          <span className="text-sm font-semibold text-foreground">Response Summary</span>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-border">
          <TrendingUp size={12} className="text-primary" />
          <span className="text-xs font-medium text-muted-foreground tabular-nums">
            {total} total responses
          </span>
        </div>
      </div>

      {/* Stat cards grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.key}
              className={`glass-card rounded-xl border p-4 fade-in-up ${stat.gradientClass} ${stat.delay}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
                  {stat.label}
                </span>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-white/5 border border-white/8`}>
                  <Icon size={15} className={stat.iconColor} />
                </div>
              </div>

              <div className="mb-3">
                <span className={`text-3xl font-bold tabular-nums ${stat.iconColor}`}>
                  {stat.count}
                </span>
              </div>

              {/* Rate bar */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">of total</span>
                  <span className={`text-xs font-semibold tabular-nums ${stat.rateColor}`}>
                    {stat.rate}%
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${stat.barColor}`}
                    style={{ width: `${stat.rate}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}